import { IMenuResponse } from "../../../../model/base/response/menu-response";

export interface NavLink {
  label: string;
  path: string;
  isExternal?: boolean;
}

export type NavChild =
  | ({ kind: "link" } & NavLink)
  | { kind: "flyout"; label: string; items: NavLink[] };

export interface NavEntry {
  /** Chave do menu fixo (tblMenu.DesChave) ou identificador do menu da entidade. */
  id: string;
  label: string;
  /** Link direto; ausente quando o menu abre dropdown. */
  path?: string;
  isExternal?: boolean;
  children?: NavChild[];
}

export const FALE_CONOSCO = "FALE_CONOSCO";
const TRANSPARENCIA = "TRANSPARENCIA";
const LICITACOES = "LICITACOES";

// Submenu próprio do portal Airdoc (não existe no catálogo de menus fixos do APM)
const gestaoFiscal: NavChild = {
  kind: "flyout",
  label: "GESTÃO FISCAL",
  items: [
    { label: "Lei de Diretrizes Orçamentárias (LDO)", path: "/publicacoes/4/40" },
    { label: "Plano Plurianual (PPA)", path: "/publicacoes/4/43" },
    { label: "Lei Orçamentária Anual (LOA)", path: "/publicacoes/4/41" },
    { label: "Relatório de Gestão Fiscal (RGF)", path: "/publicacoes/4/45" },
    { label: "Relatório Resumido da Execução Orçamentária (RREO)", path: "/publicacoes/4/46" },
  ],
};

const link = (label: string, path: string): NavChild => ({ kind: "link", label, path });

// Estrutura padrão da barra principal, usada enquanto a entidade não tem os menus fixos cadastrados no APM
export const defaultNavStructure: NavEntry[] = [
  { id: "INICIO", label: "INÍCIO", path: "/" },
  {
    id: "MUNICIPIO",
    label: "O MUNICÍPIO",
    children: [
      link("A CIDADE", "/a-cidade"),
      link("ESTRUTURA ADMINISTRATIVA", "/estrutura-administrativa"),
      link("PROJETOS GOVERNAMENTAIS", "/projetos-governamentais"),
    ],
  },
  { id: "NOTICIAS", label: "NOTÍCIAS", path: "/noticias" },
  {
    id: "DIARIO_OFICIAL",
    label: "DIÁRIO OFICIAL",
    children: [link("EDIÇÕES", "/diario-oficial"), link("PUBLICAÇÕES", "/publicacoes")],
  },
  {
    id: "SERVICOS",
    label: "SERVIÇOS",
    children: [link("PERGUNTAS FREQUENTES", "/perguntas-respostas"), link("LINKS ÚTEIS", "/links-uteis")],
  },
  {
    id: TRANSPARENCIA,
    label: "TRANSPARÊNCIA",
    children: [
      link("CONTAS PÚBLICAS", "/contas-publicas"),
      link("LICITAÇÕES", "/licitacoes"),
      gestaoFiscal,
      link("CONTRATOS E AQUISIÇÕES", "/contratos-aquisicoes"),
      link("CONVÊNIOS E REPASSES", "/convenios-repasses"),
      link("AUDITORIA", "/auditoria"),
    ],
  },
  { id: FALE_CONOSCO, label: "FALE CONOSCO", path: "/contato" },
];

function toNavEntry(menu: IMenuResponse): NavEntry | null {
  const id = menu.key || String(menu.id);
  const label = (menu.name || "").toUpperCase();

  // Menus de link direto (rota, URL ou página customizada) não abrem dropdown; os de dropdown sem itens não aparecem
  if (menu.url) return { id, label, path: menu.url, isExternal: menu.isExternal };

  const children: NavChild[] = (menu.items || [])
    .filter((item) => !!item.url)
    .map((item) => ({ kind: "link", label: (item.name || "").toUpperCase(), path: item.url!, isExternal: item.isExternal }));

  if (id === TRANSPARENCIA) {
    // Gestão Fiscal logo após Licitações (ou no fim, se a entidade removeu/reordenou)
    const index = (menu.items || []).filter((item) => !!item.url).findIndex((item) => item.key === LICITACOES);
    children.splice(index >= 0 ? index + 1 : children.length, 0, gestaoFiscal);
  }

  return children.length ? { id, label, children } : null;
}

/** Barra principal na ordem definida pela entidade no APM, ou a estrutura padrão. */
export function buildNavStructure(mainMenus: IMenuResponse[]): NavEntry[] {
  if (!mainMenus.length) return defaultNavStructure;
  return mainMenus.map(toNavEntry).filter((entry): entry is NavEntry => entry !== null);
}

/** Caminhos internos de um menu, para destacar o menu ativo. */
export function entryPaths(entry: NavEntry): string[] {
  if (entry.path) return entry.isExternal ? [] : [entry.path];
  return (entry.children || []).flatMap((child) =>
    child.kind === "link" ? (child.isExternal ? [] : [child.path]) : child.items.map((item) => item.path)
  );
}
